import {
  workbookPivotGroupSchema,
  workbookPivotValueSchema,
  type WorkbookFilter,
  type WorkbookPivotGroup,
  type WorkbookPivotSpec,
  type WorkbookPivotValue,
} from '@erp/shared-types';

/** The four areas of the pivot editor a field goes into. */
export type PivotArea = 'rows' | 'columns' | 'values' | 'filters';
export type PivotFieldHint = {
  /** The field reads as numbers, so a Value sums it rather than counting it. */
  numeric: boolean;
};

function inserted<T>(items: readonly T[], item: T, at?: number): T[] {
  const next = [...items];
  next.splice(at ?? next.length, 0, item);
  return next;
}

/**
 * Renumbers the Value each Row or Column field sorts by after the Values
 * move: `mapping` gives a Value's new index, or null once it is gone, which
 * returns the field to sorting by its labels.
 */
function remapValueSorts(
  spec: WorkbookPivotSpec,
  mapping: (index: number) => number | null,
): WorkbookPivotSpec {
  const remap = (group: WorkbookPivotGroup): WorkbookPivotGroup => {
    if (group.sortBy.by !== 'value') return group;
    const value = mapping(group.sortBy.value);
    return {
      ...group,
      sortBy:
        value === null
          ? { by: 'label', direction: 'asc' }
          : { ...group.sortBy, value },
    };
  };
  return {
    ...spec,
    rows: spec.rows.map(remap),
    columns: spec.columns.map(remap),
  };
}

function valueOf(field: string, hint: PivotFieldHint): WorkbookPivotValue {
  return workbookPivotValueSchema.parse({
    field,
    summarize: hint.numeric ? 'sum' : 'counta',
  });
}

/** A new Filter shows every item: it removes no values until one is unchecked. */
function filterOf(field: string): WorkbookFilter {
  return { column: field, operator: 'is-not-one-of', value: '', values: [] };
}

/** Adds a field to an area, at `at` when dropped between two fields. */
export function addPivotField(
  spec: WorkbookPivotSpec,
  area: PivotArea,
  field: string,
  hint: PivotFieldHint,
  at?: number,
): WorkbookPivotSpec {
  if (area === 'filters')
    return { ...spec, filters: inserted(spec.filters, filterOf(field), at) };
  if (area === 'values') {
    const index = at ?? spec.values.length;
    return remapValueSorts(
      { ...spec, values: inserted(spec.values, valueOf(field, hint), index) },
      (value) => (value >= index ? value + 1 : value),
    );
  }
  const group = workbookPivotGroupSchema.parse({ field });
  return { ...spec, [area]: inserted(spec[area], group, at) };
}

export function removePivotField(
  spec: WorkbookPivotSpec,
  area: PivotArea,
  index: number,
): WorkbookPivotSpec {
  if (area === 'values')
    return remapValueSorts(
      { ...spec, values: spec.values.filter((_, i) => i !== index) },
      (value) => (value === index ? null : value > index ? value - 1 : value),
    );
  if (area === 'filters')
    return { ...spec, filters: spec.filters.filter((_, i) => i !== index) };
  const groups = spec[area].filter((_, i) => i !== index);
  // Pinned Column keys have one label per Column field.
  return area === 'columns'
    ? pinPivotColumns({ ...spec, columns: groups }, null)
    : { ...spec, rows: groups };
}

function fieldAt(
  spec: WorkbookPivotSpec,
  area: PivotArea,
  index: number,
): string | undefined {
  if (area === 'filters') return spec.filters[index]?.column;
  return spec[area][index]?.field;
}

/**
 * Moves a field within or between areas, as dragging does. A Row or Column
 * field keeps its settings between Rows and Columns; moved to any other area
 * it becomes that area's kind of field.
 */
export function movePivotField(
  spec: WorkbookPivotSpec,
  from: { area: PivotArea; index: number },
  to: { area: PivotArea; index?: number },
  hint: PivotFieldHint,
): WorkbookPivotSpec {
  const field = fieldAt(spec, from.area, from.index);
  if (field === undefined) return spec;
  if (from.area === 'values' && to.area === 'values') {
    const moved = spec.values[from.index];
    if (!moved) return spec;
    const rest = spec.values.filter((_, i) => i !== from.index);
    const target = Math.min(to.index ?? rest.length, rest.length);
    const order = inserted(
      rest.map((_, i) => (i >= from.index ? i + 1 : i)),
      from.index,
      target,
    );
    return remapValueSorts(
      { ...spec, values: inserted(rest, moved, target) },
      (value) => order.indexOf(value),
    );
  }
  const removed = removePivotField(spec, from.area, from.index);
  const group =
    from.area === 'rows' || from.area === 'columns'
      ? spec[from.area][from.index]
      : undefined;
  if (group && (to.area === 'rows' || to.area === 'columns'))
    return {
      ...removed,
      [to.area]: inserted(removed[to.area], group, to.index),
    };
  return addPivotField(removed, to.area, field, hint, to.index);
}

/** Pins Column keys, so the pivot's columns never change with its data; null unpins. */
export function pinPivotColumns(
  spec: WorkbookPivotSpec,
  keys: readonly (readonly string[])[] | null,
): WorkbookPivotSpec {
  const { columnKeys: _unpinned, ...rest } = spec;
  return keys ? { ...rest, columnKeys: keys.map((key) => [...key]) } : rest;
}
